import Reveal from '@/components/Reveal';

const REASONS = [
  {
    num: '01',
    title: 'Education Before Recommendation',
    copy: 'We explain the reasoning before we ever propose a strategy. When you understand the "why," the decision becomes yours to own — not ours to sell.',
  },
  {
    num: '02',
    title: 'Strategy Before Product',
    copy: 'Products serve strategies. Never the other way around. We map your priorities first, then choose only the tools that fit — with nothing extra.',
  },
  {
    num: '03',
    title: 'Long-Term Relationships',
    copy: 'We are not measured by a single meeting. We are measured by the decades we walk alongside you, your spouse, your children, and the family you are building.',
  },
  {
    num: '04',
    title: 'Clarity Over Complexity',
    copy: 'The financial world thrives on complexity. Our work is to distill it into something you can hold, question, and act on with confidence.',
  },
  {
    num: '05',
    title: 'Personalized Guidance',
    copy: 'No template. No script. Every conversation begins with your life — your family, your calling, and the future you are quietly building for.',
  },
];

export default function WhyFamiliesChoose() {
  return (
    <section
      id="why-families-choose"
      data-testid="why-families-choose-section"
      className="relative bg-[#F7F4EE] py-24 md:py-36 overflow-hidden"
    >
      {/* Ambient gold accent */}
      <div
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-[0.08] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #B68A35 0%, transparent 70%)' }}
      />
      <div
        className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full opacity-[0.06] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #0E2238 0%, transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 md:mb-24">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="overline text-[#B68A35] mb-6">Why Families Choose Us</div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-serif text-[#0E2238] text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
                Why Families Choose<br />
                <span className="italic gold-text">Bowers Advisory Group</span>.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <Reveal delay={240}>
              <p className="text-[#2B2B2B]/70 text-base md:text-lg font-light leading-relaxed">
                Families do not choose us for a product. They choose us for the way we listen,
                the way we explain, and the way we stay.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Reasons — editorial numbered list */}
        <div className="divide-y divide-[#0E2238]/10 border-y border-[#0E2238]/10">
          {REASONS.map((r, i) => (
            <Reveal key={r.num} delay={i * 90}>
              <div className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 relative">
                {/* Animated left divider indicator */}
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-10 w-0 bg-[#B68A35] group-hover:w-1 transition-all duration-500" />
                <div className="md:col-span-2">
                  <span className="font-serif text-[#B68A35]/70 text-4xl md:text-5xl leading-none tracking-tight">
                    {r.num}
                  </span>
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-serif text-[#0E2238] text-2xl md:text-[1.75rem] leading-tight tracking-tight transition-transform duration-500 group-hover:translate-x-1">
                    {r.title}
                  </h3>
                </div>
                <div className="md:col-span-6">
                  <p className="text-[#2B2B2B]/75 text-base md:text-lg font-light leading-relaxed">
                    {r.copy}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Closing quote */}
        <Reveal delay={200}>
          <p className="mt-20 font-serif italic text-[#0E2238]/60 text-lg md:text-xl text-center max-w-2xl mx-auto leading-snug">
            "A legacy is not built in a single conversation.<br />
            It is built by the ones we keep having."
          </p>
        </Reveal>
      </div>
    </section>
  );
}
